import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";

export interface DevolutionsFilters {
  order_by?: 'client_name' | 'name' | 'value' | 'devolution_status_id' | 'created_at';
  order_direction?: 'asc' | 'desc';
  per_page?: number;
  client_name?: string;
  created_by_name?: string;
  devolution_status_id?: string;
}

interface DevolutionsFiltersComponentProps {
  filters: DevolutionsFilters;
  onFiltersChange: (filters: DevolutionsFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

export const DevolutionsFiltersComponent = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}: DevolutionsFiltersComponentProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const updateFilter = (key: keyof DevolutionsFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value && value !== 'all' ? value : undefined,
    });
  };

  const hasActiveFilters = () => {
    return (filters.per_page !== undefined && filters.per_page !== 50) ||
           filters.client_name ||
           filters.created_by_name ||
           filters.devolution_status_id;
  };

  return (
    <div className="w-full mb-4">
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader 
          className="pb-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <CardTitle className="text-lg font-semibold flex items-center justify-between">
            <span className="flex items-center gap-2">
              Filtros 
              {hasActiveFilters() && (
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  Ativo
                </span>
              )}
            </span>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </CardTitle>
        </CardHeader>
        
        {isExpanded && (
          <CardContent className="pt-0">
            {/* Campos de busca */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* Buscar por cliente */}
              <div className="space-y-2">
                <Label htmlFor="filter-client-name">Cliente</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="filter-client-name"
                    placeholder="Buscar por nome do cliente"
                    value={filters.client_name || ''}
                    onChange={(e) => updateFilter('client_name', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Buscar por criado por */}
              <div className="space-y-2">
                <Label htmlFor="filter-created-by-name">Criado por</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="filter-created-by-name"
                    placeholder="Buscar por nome de quem criou"
                    value={filters.created_by_name || ''}
                    onChange={(e) => updateFilter('created_by_name', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status da Devolução */}
              <div className="space-y-2">
                <Label htmlFor="filter-devolution-status">Status</Label>
                <Select
                  value={filters.devolution_status_id || 'all'}
                  onValueChange={(value) => updateFilter('devolution_status_id', value)}
                >
                  <SelectTrigger id="filter-devolution-status">
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="1">Pendente</SelectItem>
                    <SelectItem value="2">Aprovado</SelectItem>
                    <SelectItem value="3">Rejeitado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Controles de ordenação */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Itens por página */}
              <div className="space-y-2">
                <Label htmlFor="filter-per-page">Itens por página</Label>
                <Select
                  value={filters.per_page?.toString() || '50'}
                  onValueChange={(value) => 
                    updateFilter('per_page', value !== 'all' ? parseInt(value) : 50)
                  }
                >
                  <SelectTrigger id="filter-per-page">
                    <SelectValue placeholder="50" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="15">15</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Ordenar por */}
              <div className="space-y-2">
                <Label htmlFor="filter-order-by">Ordenar por</Label>
                <Select
                  value={filters.order_by || 'created_at'}
                  onValueChange={(value) => 
                    updateFilter('order_by', value as DevolutionsFilters['order_by'])
                  }
                >
                  <SelectTrigger id="filter-order-by">
                    <SelectValue placeholder="Data de criação" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client_name">Cliente</SelectItem>
                    <SelectItem value="name">Descrição</SelectItem>
                    <SelectItem value="value">Valor</SelectItem>
                    <SelectItem value="devolution_status_id">Status</SelectItem>
                    <SelectItem value="created_at">Data de criação</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Direção da ordenação */}
              <div className="space-y-2">
                <Label htmlFor="filter-order-direction">Direção</Label>
                <Select
                  value={filters.order_direction || 'desc'}
                  onValueChange={(value) => 
                    updateFilter('order_direction', value as 'asc' | 'desc')
                  }
                >
                  <SelectTrigger id="filter-order-direction">
                    <SelectValue placeholder="Decrescente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Crescente</SelectItem>
                    <SelectItem value="desc">Decrescente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Botões de ação */}
            <div className="flex gap-3">
              <Button 
                onClick={onApplyFilters} 
                className={cn("flex-1", colors.bg_primary, "hover:opacity-90")}
              >
                Aplicar Filtros
              </Button>
              <Button 
                variant="outline" 
                onClick={onClearFilters}
                className="flex-1"
              >
                Limpar Filtros
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};